import React, { useState, useEffect } from 'react';
import { getLevelInfo, checkin } from '../services/level';

/**
 * 레벨 카드 컴포넌트
 * - 사용자 드롭다운/프로필에서 상세 레벨 정보 표시
 * - 출석 체크 버튼 포함
 */
const LevelCard = ({ onLevelUp }) => {
  const [levelInfo, setLevelInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkinLoading, setCheckinLoading] = useState(false);
  const [checkinMessage, setCheckinMessage] = useState(null);

  const fetchLevelInfo = async () => {
    try {
      const data = await getLevelInfo();
      setLevelInfo(data);
    } catch (err) {
      console.error('Failed to fetch level info:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevelInfo();
  }, []);

  const handleCheckin = async () => {
    if (checkinLoading) return;

    setCheckinLoading(true);
    setCheckinMessage(null);

    try {
      const result = await checkin();
      setCheckinMessage({
        type: result.expGained > 0 ? 'success' : 'info',
        text: result.message
      });

      // 레벨업 시 콜백
      if (result.levelUp && onLevelUp) {
        onLevelUp(result.newLevel);
      }

      // 레벨 정보 새로고침
      await fetchLevelInfo();

      // 3초 후 메시지 제거
      setTimeout(() => setCheckinMessage(null), 3000);
    } catch (err) {
      setCheckinMessage({
        type: 'error',
        text: '출석 체크에 실패했습니다'
      });
    } finally {
      setCheckinLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-3 bg-gradient-to-br from-gray-50 to-slate-100 rounded-[10px] border border-slate-200 min-h-[180px]">
        <div className="w-full h-full bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 bg-[length:200%_100%] animate-[skeleton-loading_1.5s_infinite] rounded-lg"></div>
      </div>
    );
  }

  if (!levelInfo) {
    return null;
  }

  const { level, currentExp, nextLevelExp, totalExp, progress, title, stats } = levelInfo;

  return (
    <div className="p-3 bg-gradient-to-br from-gray-50 to-slate-100 rounded-[10px] border border-slate-200">
      {/* 레벨 헤더 */}
      <div className="flex items-center gap-2.5 mb-3">
        <div className="text-[28px] leading-none">{title?.icon || '👀'}</div>
        <div className="flex flex-col gap-px">
          <div className="text-base md:text-lg font-bold text-gray-900">Lv.{level}</div>
          <div className="text-xs text-slate-500 font-medium">{title?.name || '문파 구경꾼'}</div>
        </div>
      </div>

      {/* 경험치 바 */}
      <div className="mb-2.5">
        <div className="flex justify-between items-center mb-1">
          <span className="text-[11px] text-slate-500 font-medium">경험치</span>
          <span className="text-[11px] text-blue-500 font-semibold">
            {currentExp.toLocaleString()} / {nextLevelExp.toLocaleString()}
          </span>
        </div>
        <div className="h-1.5 bg-slate-200 rounded-sm overflow-hidden mb-1">
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-blue-400 to-blue-300 rounded transition-[width] duration-500 ease-out relative after:content-[''] after:absolute after:inset-0 after:bg-gradient-to-r after:from-transparent after:via-white/30 after:to-transparent after:animate-[shimmer_2s_infinite]"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="text-[11px] text-slate-400 text-right">
          총 {totalExp.toLocaleString()} EXP
        </div>
      </div>

      {/* 통계 */}
      <div className="flex gap-2 mb-2.5 p-2 md:p-2.5 bg-white rounded-md border border-slate-100">
        <div className="flex-1 flex flex-col items-center gap-px">
          <span className="text-sm">🔥</span>
          <span className="text-[13px] md:text-sm font-bold text-gray-900">{stats?.loginStreak || 0}일</span>
          <span className="text-[9px] text-slate-400 font-medium">연속 접속</span>
        </div>
        <div className="flex-1 flex flex-col items-center gap-px">
          <span className="text-sm">📅</span>
          <span className="text-[13px] md:text-sm font-bold text-gray-900">{stats?.totalLogins || 0}회</span>
          <span className="text-[9px] text-slate-400 font-medium">총 접속</span>
        </div>
      </div>

      {/* 출석 체크 버튼 */}
      <button
        className={`w-full py-2 px-3 bg-gradient-to-br from-blue-500 to-blue-600 text-white border-none rounded-md text-[13px] font-semibold cursor-pointer flex items-center justify-center gap-1.5 transition-all duration-200 hover:from-blue-600 hover:to-blue-700 hover:-translate-y-px hover:shadow-lg hover:shadow-blue-500/30 active:translate-y-0 disabled:opacity-70 disabled:cursor-not-allowed ${checkinLoading ? 'bg-slate-400' : ''}`}
        onClick={handleCheckin}
        disabled={checkinLoading}
      >
        {checkinLoading ? (
          <span className="text-white/80">처리 중...</span>
        ) : (
          <>
            <span className="text-sm">✅</span>
            <span>출석 체크</span>
          </>
        )}
      </button>

      {/* 출석 메시지 */}
      {checkinMessage && (
        <div className={`mt-2 py-1.5 px-2.5 rounded-md text-[11px] font-medium text-center animate-[fadeInUp_0.3s_ease] ${
          checkinMessage.type === 'success'
            ? 'bg-green-100 text-green-800 border border-green-200'
            : checkinMessage.type === 'info'
            ? 'bg-blue-100 text-blue-800 border border-blue-200'
            : 'bg-red-100 text-red-800 border border-red-200'
        }`}>
          {checkinMessage.text}
        </div>
      )}
    </div>
  );
};

export default LevelCard;
